import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './TheVesselDesign.css';

const imageBase = '/images/my upset tracker asset';

const content = {
  en: {
    back: '← My Work',
    heroKicker: 'iOS App · Mood Journal',
    heroTitle: 'Vessel',
    heroSub: 'A vessel that holds whatever you are feeling.',
    heroBody1:
      'This app tries to do something quiet — when you are at your lowest, or your happiest, it helps you mark the moment. You do not have to explain it to anyone, and you do not have to feel better right away. All you need is to pause once a day and tell yourself, on a scale from 1 to 10, "this is where I am right now."',
    heroBody2:
      'Over time, those small entries become a map of your inner weather — not so you can compare your "good" and "bad" days, but so you can finally see and listen to your own voice.',
    whyKicker: 'Design Considerations Across the App',
    whyTitle: '',
    whyP1:
      'Vessel was designed for users who want a private and low-pressure way to record their thoughts, feelings, and daily experiences. It is for people who enjoy journaling, people who want to better understand their personal patterns over time, and anyone who wants a quiet space to capture meaningful moments — whether they are positive, difficult, or simply part of everyday life.',
    whyP2: '',
    designKicker: '',
    designTitle: 'Visual Direction for App Store Posters',
    designPoster: '/images/poster for Apple store.png',
    designIntro:
      'Each poster was created to communicate a different layer of Vessel’s identity — from quick daily recording, to reflective pattern awareness, to the quieter emotional tone behind the product. Rather than presenting the posters as simple screenshots, composition, color, lighting, and visual metaphor were used to make the app feel calm, private, and emotionally approachable.',
    posterNameLabel: 'Poster',
    designCards: [
      {
        id: 'journaling',
        title: 'Warmth as the First Impression',
        images: ['/images/封面1.png'],
        points: [
          {
            label: 'Design choice',
            body: 'A warm neutral color palette, soft sand-toned textures, gentle shadows, and natural objects were used to create a calm everyday setting. The plant adds a fresh and natural touch, while the slightly tilted phone mockup gives the poster a casual, personal, and approachable feel.',
          },
          {
            label: 'Target audience',
            body: 'This poster was created for people who want a private and simple way to record thoughts, feelings, and everyday moments. The warm visual tone helps reflection feel easy to start, making journaling feel personal, approachable, and natural to fit into daily life.',
          },
        ],
      },
      {
        id: 'patterns',
        title: 'A Visual Language for Reflection',
        images: ['/images/封面2.png', '/images/封面3.png'],
        points: [
          {
            label: 'Design choice',
            body: 'Darker interface screens, calendar blocks, and chart visuals were used to express long-term reflection. The calendar poster frames each date as part of a personal timeline, while the chart poster highlights rhythm, movement, and change over time.',
          },
          {
            label: 'Target audience',
            body: 'These posters were created for people who want to look back on daily records and better understand their feelings, routines, and personal patterns over time. The visual direction keeps reflection clear, simple, and easy to follow.',
          },
        ],
      },
      {
        id: 'storytelling',
        title: 'Visual Storytelling: A Girl Holding a Small Light',
        images: ['/images/文字和小女孩修正.png'],
        points: [
          {
            label: 'Design choice',
            body: 'A late-night timestamp in the top-left corner, a quiet sea, a small boat, and a girl holding a lantern were used to create a visual metaphor for self-reflection. The time suggests a private moment when the world is quiet and the user is alone with their thoughts. The large dark space creates stillness, solitude, and uncertainty, while the small warm light becomes the emotional focus of the poster.',
          },
          {
            label: 'Target audience',
            body: 'This poster was created for people who may feel vulnerable, tired, or emotionally uncertain. The girl represents the deeper and more honest part of the self, while the lantern suggests gentle direction, quiet support, and the possibility of moving forward at one’s own pace.',
          },
        ],
      },
    ],
    showcaseKicker: 'Inside the App',
    showcaseTitle: 'Four small corners, four different vessels for emotion.',

    quote:
      '\u201cYour mental attitude determines what you make of it, either a stepping stone or a stumbling block.\u201d',

    footerBack: '← Back to My Work',
    footerNote: 'Vessel · 2026',
    slides: [
      {
        eyebrow: 'Today',
        title: 'Marking how you feel right now',
        body: 'Each day is split into two parts — morning and afternoon. Tap a number from 1 to 10 to leave the most honest read of how you feel, or write a short note to put down whatever you couldn\u2019t say out loud today. Quietly, this space holds it for you.',
      },
      {
        eyebrow: 'Calendar',
        title: 'Looking back at every day',
        body: 'The mood calendar paints each day with a color: very good, okay, slightly low, low, and days with bigger swings. Looking back, you can see which days carried more weight, and which days felt lighter — and gently understand the season of yourself you were living through.',
      },
      {
        eyebrow: 'Evaluation',
        title: 'Seeing the shape of your emotions',
        body: 'The Daily Mood Chart connects morning and afternoon scores into a single line, marking days that have notes with little icons. Feelings are not always linear — sometimes happy, sometimes low, sometimes calm. Here you can see the curve of your own emotions, and how it slowly shifts.',
      },
      {
        eyebrow: 'Story',
        title: 'Heart Trail — a corner just for you',
        body: 'Heart Trail is a private space that belongs only to you. You can write a paragraph, sketch something, record a voice note, or even capture a short video. Nothing has to be sent, understood, or replied to — it only gives that emotion somewhere to rest.',
      },
    ],
  },
  zh: {
    back: '← 我的作品',
    heroKicker: 'iOS App · 情緒紀錄',
    heroTitle: 'My Upset Tracker (Vessel)',
    heroSub: '一個承托情緒的容器。',
    heroBody1:
      '這個 app 想做的事情很簡單 — 在情緒最沉的時候，或者是你開心的時候，幫你把那一刻記下來。不需要解釋給誰聽，也不需要立刻變得更好。只要每天願意停下來一次，用 1 到 10 的數字告訴自己：「我現在的狀態，是這樣。」',
    heroBody2:
      '時間久了，這些紀錄會變成一張屬於你的情緒地圖。不是為了比較哪天比較好，而是為了讓自己看見，讓自己聽見自己的聲音。',
    whyKicker: '整體 App 的設計考量',
    whyTitle: '',
    whyP1:
      'Vessel 是為想要以私密、低壓方式記錄想法、感受與日常經驗的使用者而設計。它適合喜歡書寫日記的人、希望長期理解自己個人模式的人，也適合任何想要一個安靜空間來保存重要時刻的人 — 無論那些時刻是正面的、困難的，或只是日常生活的一部分。',
    whyP2: '',
    designKicker: '',
    designTitle: 'App Store 海報的視覺方向',
    designPoster: '/images/poster for Apple store.png',
    designIntro:
      '每一張海報都用來傳達 Vessel 身分中的不同層次：從快速的日常紀錄、對長期模式的覺察，到產品背後更安靜的情緒語氣。這些海報沒有只是單純呈現 app 截圖，而是透過構圖、色彩、光線與視覺隱喻，讓產品感覺平靜、私密，並且在情緒上更容易靠近。',
    posterNameLabel: '海報',
    designCards: [
      {
        id: 'journaling',
        title: '以溫度作為第一印象',
        images: ['/images/封面1.png'],
        points: [
          {
            label: '設計選擇',
            body: '我使用溫暖的中性色調、柔和的沙色質感、輕微陰影與自然物件，建立一個平靜而貼近日常的場景。植物帶來清新自然的感覺，而微微傾斜的手機展示畫面則讓海報呈現出更隨性、個人化且容易接近的氛圍。',
          },
          {
            label: '目標受眾',
            body: '這張海報是為想要用私密且簡單的方式記錄想法、感受與日常片刻的人而設計。溫暖的視覺語氣讓反思看起來更容易開始，也讓日記書寫變得更個人、更親近，並自然融入日常生活。',
          },
        ],
      },
      {
        id: 'patterns',
        title: '為反思建立視覺語言',
        images: ['/images/封面2.png', '/images/封面3.png'],
        points: [
          {
            label: '設計選擇',
            body: '我使用較深色的介面畫面、日曆區塊與圖表視覺來表達長期反思。日曆海報把每一天視為個人時間線的一部分，而圖表海報則強調情緒節奏、移動感，以及隨時間產生的變化。',
          },
          {
            label: '目標受眾',
            body: '這組海報是為希望回看日常紀錄，並隨時間更理解自身感受、生活習慣與個人模式的人而設計。整體視覺方向讓反思保持清楚、簡單，也更容易被持續追蹤。',
          },
        ],
      },
      {
        id: 'storytelling',
        title: '視覺敘事：手捧微光的女孩',
        images: ['/images/文字和小女孩修正.png'],
        points: [
          {
            label: '設計選擇',
            body: '左上角的深夜時間、安靜的海面、小船，以及手捧燈籠的女孩，被用來建立一個關於自我反思的視覺隱喻。時間暗示著世界安靜下來、使用者獨自面對想法的私密時刻。大片黑暗空間帶出靜止、孤獨與不確定感，而那一點溫暖的光，則成為整張海報的情緒焦點。',
          },
          {
            label: '目標受眾',
            body: '這張海報是為那些可能感到脆弱、疲憊，或在情緒上不確定的人而設計。女孩象徵自我中更深、更誠實的部分，而燈籠則暗示溫柔的方向、安靜的支持，以及依照自己步調慢慢往前走的可能。',
          },
        ],
      },
    ],
    showcaseKicker: 'App 內部體驗',
    showcaseTitle: '四個小角落，不同的情緒容器。',

    quote:
      '「你的心態決定了你怎麼看待它 —\n是一塊往前走的踏腳石，還是一塊讓你絆倒的石頭。」',

    footerBack: '← 回到我的作品',
    footerNote: 'Vessel · 2026',
    slides: [
      {
        eyebrow: '今日',
        title: '紀錄此刻的心情',
        body: '每一天分成兩個段落 — 上午與下午。點擊 1 到 10 的能量條，留下這個時段最真實的感受。或者寫一段短短的心情筆記，把今天說不出口的事情，安靜地交給這個空間。',
      },
      {
        eyebrow: '日曆',
        title: '回顧每一天的情緒',
        body: '情緒日曆把每一天的心情上色：很好、還好、偏低、低落，還有波幅較大的日子。回頭看的時候，可以看到自己在哪些日子比較沉，哪些日子比較開心。',
      },
      {
        eyebrow: '分析',
        title: '看見情緒的軌跡',
        body: 'Daily Mood Chart 把上午與下午的分數連成一條折線，用顏色和圖示標記有筆記的日子。情緒不一定是線性的，可能開心，可能低落，也可能平靜。你可以看到自己的情緒軌跡及筆記，也可以看到自己的情緒如何變化。',
      },
      {
        eyebrow: '心跡',
        title: '心跡 — 留給自己的角落',
        body: '心跡是一個只屬於自己的私人空間。可以寫下一段文字、塗鴉、錄一段聲音，甚至錄一段影像。不用發出去，不用被理解，也不用被回應。只是讓那個情緒，有地方安放。',
      },
    ],
  },
};

const slideImages = [
  `${imageBase}/IMG_7799.jpg`,
  `${imageBase}/IMG_7800.PNG`,
  `${imageBase}/IMG_7801.PNG`,
  `${imageBase}/IMG_7802.PNG`,
];

function RevealBlock({ children, className = '', delay = 0 }) {
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          el.classList.add('visible');
          observer.unobserve(el);
        }
      },
      { threshold: 0.16 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      className={`tvd-reveal ${className}`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {children}
    </div>
  );
}

export default function TheVesselDesign() {
  const navigate = useNavigate();
  const [activeSlide, setActiveSlide] = useState(0);
  const [lang, setLang] = useState('en');
  const [lightboxImage, setLightboxImage] = useState(null);
  const t = content[lang];
  const slide = { ...t.slides[activeSlide], image: slideImages[activeSlide] };
  const total = t.slides.length;
  const designPosterCards = t.designCards.filter(
    (card) => card.id !== 'storytelling'
  );
  const storytellingCard = t.designCards.find(
    (card) => card.id === 'storytelling'
  );

  const goPrev = () => setActiveSlide((c) => (c === 0 ? total - 1 : c - 1));
  const goNext = () => setActiveSlide((c) => (c === total - 1 ? 0 : c + 1));
  const toggleLang = () => setLang((l) => (l === 'en' ? 'zh' : 'en'));

  useEffect(() => {
    if (!lightboxImage) return undefined;
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') setLightboxImage(null);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [lightboxImage]);

  return (
    <div className="tvd-page" lang={lang}>
      <div className="tvd-rain" aria-hidden="true">
        {Array.from({ length: 40 }).map((_, i) => (
          <span
            key={i}
            style={{
              left: `${(i * 2.7) % 100}%`,
              animationDelay: `${(i * 0.31) % 6}s`,
              animationDuration: `${5 + ((i * 0.7) % 4)}s`,
            }}
          />
        ))}
      </div>

      <button className="tvd-back" onClick={() => navigate('/my-work')}>
        {t.back}
      </button>

      <button
        className="tvd-lang"
        onClick={toggleLang}
        aria-label="Toggle language"
        title={lang === 'en' ? '切換到中文' : 'Switch to English'}
      >
        <svg
          className="tvd-lang-icon"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.6"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <circle cx="12" cy="12" r="9" />
          <path d="M3 12h18" />
          <path d="M12 3a14 14 0 0 1 0 18" />
          <path d="M12 3a14 14 0 0 0 0 18" />
        </svg>
        <span className="tvd-lang-label">{lang === 'en' ? '中文' : 'EN'}</span>
      </button>

      {/* Hero */}
      <section className="tvd-hero">
        <div className="tvd-hero-haze tvd-hero-haze-a" />
        <div className="tvd-hero-haze tvd-hero-haze-b" />

        <RevealBlock className="tvd-hero-grid">
          <div className="tvd-hero-copy">
            <span className="tvd-kicker">{t.heroKicker}</span>
            <h1 className="tvd-hero-title">{t.heroTitle}</h1>
            <p className="tvd-hero-sub">{t.heroSub}</p>
            <p className="tvd-hero-body">{t.heroBody1}</p>
            <p className="tvd-hero-body">{t.heroBody2}</p>
            <div className="tvd-tech-row">
              {['React Native', 'Expo', 'Local Storage', 'Charts'].map(
                (tech) => (
                  <span key={tech}>{tech}</span>
                )
              )}
            </div>
          </div>

          <div className="tvd-icon-stage">
            <div className="tvd-icon-orbit" />
            <img
              src={`${imageBase}/ChatGPT Image Apr 29, 2026 at 01_43_29 PM.webp`}
              alt="My Upset Tracker app icon"
              className="tvd-app-icon"
            />
          </div>
        </RevealBlock>
      </section>

      {/* Design considerations */}
      <section className="tvd-why">
        <RevealBlock className="tvd-why-card">
          <span className="tvd-kicker">{t.whyKicker}</span>
          {t.whyTitle && <h2>{t.whyTitle}</h2>}
          <p>{t.whyP1}</p>
          {t.whyP2 && <p>{t.whyP2}</p>}
        </RevealBlock>
      </section>

      {/* Product design notes */}
      <section className="tvd-design-notes">
        <RevealBlock className="tvd-design-notes-header">
          {t.designKicker && (
            <span className="tvd-kicker">{t.designKicker}</span>
          )}
          <h2>{t.designTitle}</h2>
        </RevealBlock>

        <RevealBlock className="tvd-design-poster">
          <button
            className="tvd-design-poster-button"
            type="button"
            onClick={() => setLightboxImage(t.designPoster)}
            aria-label="Open App Store poster image"
          >
            <img src={t.designPoster} alt={t.designTitle} />
          </button>
        </RevealBlock>

        <RevealBlock className="tvd-design-intro">
          <p>{t.designIntro}</p>
        </RevealBlock>

        <RevealBlock className="tvd-design-grid">
          {designPosterCards.map((card) => (
            <article
              className={`tvd-design-card tvd-design-card-${card.id}`}
              key={card.id}
            >
              <div className="tvd-design-card-media">
                {card.images.map((image) => (
                  <button
                    className="tvd-design-image-button"
                    key={image}
                    type="button"
                    onClick={() => setLightboxImage(image)}
                    aria-label={`Open ${card.title} image`}
                  >
                    <img src={image} alt={card.title} />
                  </button>
                ))}
              </div>

              <div className="tvd-design-card-copy">
                <span className="tvd-design-poster-name">
                  {t.posterNameLabel}
                </span>
                <h3>"{card.title}"</h3>
                {card.points.map((point) => (
                  <div className="tvd-design-point" key={point.label}>
                    <span>{point.label}</span>
                    <p>{point.body}</p>
                  </div>
                ))}
              </div>
            </article>
          ))}
        </RevealBlock>

        {storytellingCard && (
          <RevealBlock className="tvd-design-story-feature">
            <article className="tvd-design-card tvd-design-card-storytelling">
              <div className="tvd-design-card-media">
                {storytellingCard.images.map((image) => (
                  <button
                    className="tvd-design-image-button"
                    key={image}
                    type="button"
                    onClick={() => setLightboxImage(image)}
                    aria-label={`Open ${storytellingCard.title} image`}
                  >
                    <img src={image} alt={storytellingCard.title} />
                  </button>
                ))}
              </div>

              <div className="tvd-design-card-copy">
                <span className="tvd-design-poster-name">
                  {t.posterNameLabel}
                </span>
                <h3>"{storytellingCard.title}"</h3>
                {storytellingCard.points.map((point) => (
                  <div className="tvd-design-point" key={point.label}>
                    <span>{point.label}</span>
                    <p>{point.body}</p>
                  </div>
                ))}
              </div>
            </article>
          </RevealBlock>
        )}
      </section>

      {/* Showcase */}
      <section className="tvd-showcase">
        <RevealBlock className="tvd-showcase-header">
          <span className="tvd-kicker">{t.showcaseKicker}</span>
          <h2>{t.showcaseTitle}</h2>
        </RevealBlock>

        <RevealBlock className="tvd-showcase-stage">
          <div className="tvd-phone-panel">
            <div className="tvd-phone-frame">
              <img src={slide.image} alt={slide.title} />
            </div>
          </div>

          <div className="tvd-slide-copy">
            <span className="tvd-slide-count">
              {String(activeSlide + 1).padStart(2, '0')} /{' '}
              {String(total).padStart(2, '0')}
            </span>
            <span className="tvd-slide-eyebrow">{slide.eyebrow}</span>
            <h3>{slide.title}</h3>
            <p>{slide.body}</p>

            <div className="tvd-controls">
              <button onClick={goPrev} aria-label="Previous slide">
                ←
              </button>
              <div className="tvd-dots">
                {t.slides.map((item, idx) => (
                  <button
                    key={item.title}
                    className={idx === activeSlide ? 'active' : ''}
                    onClick={() => setActiveSlide(idx)}
                    aria-label={`Go to ${item.title}`}
                  />
                ))}
              </div>
              <button onClick={goNext} aria-label="Next slide">
                →
              </button>
            </div>
          </div>
        </RevealBlock>
      </section>

      {/* Quote */}
      <section className="tvd-quote">
        <RevealBlock className="tvd-quote-block">
          <span className="tvd-kicker">{t.quoteKicker}</span>
          <p className="tvd-quote-text">{t.quote}</p>
          <span className="tvd-quote-author">{t.quoteAuthor}</span>
        </RevealBlock>
      </section>

      {/* Footer */}
      <footer className="tvd-footer">
        <button
          className="tvd-footer-back"
          onClick={() => navigate('/my-work')}
        >
          {t.footerBack}
        </button>
        <p>{t.footerNote}</p>
      </footer>

      {lightboxImage && (
        <div
          className="tvd-lightbox"
          role="dialog"
          aria-modal="true"
          aria-label="Expanded design image"
          onClick={() => setLightboxImage(null)}
        >
          <button
            className="tvd-lightbox-close"
            type="button"
            onClick={() => setLightboxImage(null)}
            aria-label="Close expanded image"
          >
            ×
          </button>
          <img
            src={lightboxImage}
            alt="Expanded design preview"
            onClick={(event) => event.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
}
